import styled, { css } from 'styled-components';

import { Text } from '../../../../components/Text/Text';
import { COLORS } from '../../../../theme/tokens';
import { hoverWash } from '../../../../theme/mixins';

/* The Lumio generate CTA — a filled primary button that carries its credit cost inline (so the cost
   reads on the action itself rather than as a tagline beneath). Mirrors the shared primary Button
   (fill, height, radius, hoverWash); a centred flex row holds the label, a subtle divider, and the
   cost. */
export const Root = styled.button<{ $compact?: boolean; $secondary?: boolean }>`
  ${hoverWash}
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  /* Compact variant: size to content (a shared bar CTA) rather than filling the row. */
  ${(p) =>
    p.$compact &&
    css`
      width: auto;
    `}
  box-sizing: border-box;
  height: 48px;
  padding: 0 16px;
  border-radius: 99px;
  cursor: pointer;
  background: ${COLORS.ctaSolid};
  border: 1px solid ${COLORS.ctaSolid};
  color: ${COLORS.ctaSolidText};
  font-family: inherit;
  /* Secondary: the shared outline Button's surface, for a Lumio action that is not the screen's main one. */
  ${(p) =>
    p.$secondary &&
    css`
      background: ${COLORS.ctaOutlineBg};
      border-color: ${COLORS.ctaBorder};
      color: ${COLORS.ctaText};
    `}

  &:disabled {
    cursor: default;
    opacity: 0.45;
  }
  /* A disabled CTA is inert — suppress the glow even though :hover still fires. */
  &:disabled:hover::before,
  &:disabled:hover::after,
  &:disabled:focus-visible::before,
  &:disabled:focus-visible::after {
    opacity: 0;
  }
`;

/* The action label. */
export const Label = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;
`;

/* The inline cost: solid Lumio spark + "{n} credits", in the button's text colour. */
export const CostGroup = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

/* The divider between the label and the cost — the button's text colour, softened so it reads as a
   subtle separator rather than a second element. */
export const Sep = styled.span`
  opacity: 0.5;
  font-weight: 400;
`;

export const CostText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 14px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  white-space: nowrap;
`;
